import { useEffect, useRef } from 'react';
import { useDebouncedConfigUpdates } from './use-debounced-config-updates';
import { toast } from '../util/toasts';

interface UseAutoSaveConfigsOptions {
  /**
   * Whether auto-save is enabled (set to true after initial load)
   * @default true
   */
  enabled: boolean;

  /**
   * Whether the current data is valid and can be saved.
   * Can be:
   * - boolean: true = save all, false = block all
   * - Record<string, boolean>: per-field validation (only save valid fields)
   *
   * @example
   * ```tsx
   * // Block all if any validation fails
   * isValid: false
   *
   * // Per-field validation (recommended)
   * isValid: {
   *   CONF_ENABLED: !errors.enabled,
   * }
   * ```
   * @default true
   */
  isValid?: boolean | Record<string, boolean>;

  /**
   * Delay in milliseconds before triggering the update
   * @default 800
   */
  delay?: number;

  /**
   * Mapping of config variable names to setter functions for automatic revert on error.
   * When a save fails, the hook will automatically call the setter with the last saved value.
   *
   * @example
   * ```tsx
   * setters: {
   *   CONF_BOOLEAN_EXAMPLE: (value) => setExample(value === '1'),
   *   CONF_NUMBER_EXAMPLE: (value) => setExample(parseFloat(value)),
   * }
   * ```
   */
  setters?: Record<string, (value: string) => void>;

  /**
   * Show a toast message when the update succeeds
   */
  showSuccessToast?: boolean;

  /**
   * Show a toast message when the update fails
   */
  showErrorToast?: boolean;

  /**
   * Callback fired when update succeeds
   */
  onSuccess?: (variable: string, value: string) => void;

  /**
   * Callback fired when update fails.
   * If setters are provided, automatic revert happens before this callback.
   */
  onError?: (error: any, variable: string, attemptedValue: string, lastSavedValue: string) => void;
}

interface UseAutoSaveConfigsReturn {
  /**
   * Whether an update is currently in progress
   */
  isSaving: boolean;

  /**
   * Whether there are pending updates waiting to be executed
   */
  hasPendingChanges: boolean;

  /**
   * Cancel any pending updates (useful for modal close, form reset, etc.)
   */
  cancelPendingUpdates: () => void;
}

/**
 * Automatically save configurations when their values change.
 * Handles debouncing and prevents saves on initial render.
 *
 * @example
 * ```tsx
 * const [enabled, setEnabled] = useState(false);
 * const [value, setValue] = useState(0);
 * const [isLoaded, setIsLoaded] = useState(false);
 *
 * // Load initial values
 * useEffect(() => {
 *   loadConfigs().then(data => {
 *     setEnabled(data.enabled);
 *     setValue(data.value);
 *     setIsLoaded(true);
 *   });
 * }, []);
 *
 * // Validate data with Zod or other custom validation
 * const { errors } = useZodValidation(schema, { enabled, value });
 *
 * // Auto-save when values change (all configs in one place!)
 * const { isSaving, hasPendingChanges, cancelPendingUpdates } = useAutoSaveConfigs({
 *   'CONF_ENABLED': enabled ? '1' : '0',
 *   'CONF_VALUE': value.toString(),
 * }, {
 *   enabled: isLoaded,
 *   // Per-field validation - only blocks invalid fields
 *   isValid: {
 *     CONF_ENABLED: !errors.enabled, // Only block if this field has error
 *     // CONF_VALUE will save even if CONF_ENABLED is invalid
 *   },
 *   delay: 800,
 *   // Automatic revert on error - no switch statement needed!
 *   setters: {
 *     CONF_ENABLED: (value) => setEnabled(value === '1'),
 *     CONF_VALUE: (value) => setValue(parseFloat(value)),
 *   },
 *   showErrorToast: true,
 * });
 *
 * // Use hasPendingChanges and cancelPendingUpdates for unsaved changes warnings
 * const handleClose = () => {
 *   if (hasPendingChanges) {
 *     if (confirm('Discard unsaved changes?')) {
 *       cancelPendingUpdates();
 *       onClose();
 *     }
 *   } else {
 *     onClose();
 *   }
 * };
 * ```
 */
export const useAutoSaveConfigs = (
  configs: Record<string, string>,
  options: UseAutoSaveConfigsOptions,
): UseAutoSaveConfigsReturn => {
  const {
    enabled = true,
    isValid = true,
    delay = 800,
    onSuccess,
    onError,
    setters,
    showSuccessToast,
    showErrorToast,
  } = options;

  // Track previous values for all configs
  const prevConfigsRef = useRef<Record<string, string>>(configs);
  const isFirstRender = useRef(true);
  const prevEnabledRef = useRef(enabled);

  // Track last successfully saved values (for reverting on error)
  const lastSavedConfigsRef = useRef<Record<string, string>>(configs);

  const { updateConfig, isUpdating, hasPendingUpdates, cancelPendingUpdates } = useDebouncedConfigUpdates({
    delay,
    trackLoading: true,
    onSuccess: (variable, value) => {
      // Update last saved value on success
      lastSavedConfigsRef.current[variable] = value;
      if (showSuccessToast) {
        toast('Configuration saved successfully', 'success');
      }
      onSuccess?.(variable, value);
    },
    onError: (error, variable, attemptedValue) => {
      const lastSavedValue = lastSavedConfigsRef.current[variable] || '';

      // Automatic revert if setter is provided
      if (setters && setters[variable]) {
        try {
          setters[variable](lastSavedValue);
        } catch (setterError) {
          console.error(`Failed to revert ${variable}:`, setterError);
        }
      }

      // Call custom error handler
      if (showErrorToast) {
        toast('Failed to save configuration', 'error');
      }
      onError?.(error, variable, attemptedValue, lastSavedValue);
    },
  });

  useEffect(() => {
    // Skip on first render
    if (isFirstRender.current) {
      isFirstRender.current = false;
      prevConfigsRef.current = configs;
      lastSavedConfigsRef.current = configs;
      prevEnabledRef.current = enabled;
      return;
    }

    // If enabled just transitioned from false to true, update ref without saving
    if (!prevEnabledRef.current && enabled) {
      prevConfigsRef.current = configs;
      lastSavedConfigsRef.current = configs; // Initialize with loaded values
      prevEnabledRef.current = enabled;
      return;
    }

    // Update the enabled ref
    prevEnabledRef.current = enabled;

    // Skip if disabled
    if (!enabled) {
      prevConfigsRef.current = configs;
      return;
    }

    // Determine if we should use field-level or global validation
    const isFieldLevelValidation = typeof isValid === 'object';
    const globalValidation = typeof isValid === 'boolean' ? isValid : true;

    // If global validation fails, block all saves
    if (!globalValidation) {
      prevConfigsRef.current = configs;
      return;
    }

    // Find and update only changed configs
    Object.entries(configs).forEach(([variable, value]) => {
      const prevValue = prevConfigsRef.current[variable];
      const lastSavedValue = lastSavedConfigsRef.current[variable];

      // Check field-level validation if provided
      if (isFieldLevelValidation) {
        const fieldIsValid = isValid[variable] !== false; // undefined = valid, true = valid, false = invalid
        if (!fieldIsValid) {
          return; // Skip this field, it's invalid
        }
      }

      // Only save if value changed AND it's different from last saved value
      // This prevents saving after a revert
      if (prevValue !== value && value !== lastSavedValue) {
        updateConfig(variable, value);
      }
    });

    // Update previous values
    prevConfigsRef.current = configs;
  }, [configs, enabled, isValid, updateConfig]);

  return {
    isSaving: isUpdating,
    hasPendingChanges: hasPendingUpdates,
    cancelPendingUpdates,
  };
};
